import {
  createSearchParamsCache,
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
} from 'nuqs/server';
import { z } from 'zod';

import {
  driveTypeOptions,
  otherOptions,
} from '@/constants/filters/filters.constants';
import {
  DealershipsByProvince,
  fetchDealershipsByProvince,
} from '@/services/filters/new-filters.service';
import { VehicleType } from '@/types/used-cars/used-cars.types';

export const searchParamsDefaults = {
  page: 1,
  sort: '-date_activated',
  makemodel: [],
  locations: [],
  bodytype: [],
  colours: [],
  doors: [],
  features: [],
  pricefrom: null,
  priceto: null,
  yearfrom: null,
  yearto: null,
  mileagefrom: null,
  mileageto: null,
  transmission: null,
  drivetype: null,
  province: [],
  dealership: [],
  fueltype: [],
  fueleconomyfrom: null,
  fueleconomyto: null,
  type: 'all',
  search: '',
};

export const searchParamsItems = {
  page: parseAsInteger.withDefault(searchParamsDefaults.page),
  sort: parseAsString.withDefault(searchParamsDefaults.sort),
  makemodel: parseAsArrayOf(parseAsString).withDefault(
    searchParamsDefaults.makemodel,
  ),
  locations: parseAsArrayOf(parseAsString).withDefault(
    searchParamsDefaults.locations,
  ),
  bodytype: parseAsArrayOf(parseAsString).withDefault(
    searchParamsDefaults.bodytype,
  ),
  colours: parseAsArrayOf(parseAsString).withDefault(
    searchParamsDefaults.colours,
  ),
  doors: parseAsArrayOf(parseAsString).withDefault(searchParamsDefaults.doors),
  features: parseAsArrayOf(parseAsString).withDefault(
    searchParamsDefaults.features,
  ),
  pricefrom: parseAsInteger,
  priceto: parseAsInteger,
  yearfrom: parseAsInteger,
  yearto: parseAsInteger,
  mileagefrom: parseAsInteger,
  mileageto: parseAsInteger,
  transmission: parseAsArrayOf(parseAsString),
  drivetype: parseAsArrayOf(parseAsInteger),
  province: parseAsArrayOf(parseAsString).withDefault(
    searchParamsDefaults.province,
  ),
  dealership: parseAsArrayOf(parseAsString).withDefault(
    searchParamsDefaults.dealership,
  ),
  fueltype: parseAsArrayOf(parseAsString),
  fueleconomyfrom: parseAsInteger,
  fueleconomyto: parseAsInteger,
  type: parseAsString.withDefault(searchParamsDefaults.type),
  search: parseAsString.withDefault(searchParamsDefaults.search),
};

export const searchParamsCache = createSearchParamsCache(searchParamsItems);

export const ParamsSchema = z.object({
  page: z.number(),
  sort: z.union([
    z.literal('-date_activated'),
    z.literal('display_price'),
    z.literal('-display_price'),
    z.literal('mileage'),
    z.literal('-mileage'),
    z.literal('year'),
    z.literal('-year'),
  ]),
  makemodel: z.array(z.string()),
  bodytype: z.array(z.string()),
  colours: z.array(z.string()),
  doors: z.array(z.string()),
  pricefrom: z.number().nullish(),
  priceto: z.number().nullish(),
  yearfrom: z.number().nullish(),
  yearto: z.number().nullish(),
  mileagefrom: z.number().nullish(),
  mileageto: z.number().nullish(),
  province: z
    .array(
      z.union([
        z.literal('gauteng'),
        z.literal('western cape'),
        z.literal('eastern cape'),
        z.literal('northern cape'),
        z.literal('kwazulu-natal'),
        z.literal('limpopo'),
        z.literal('north west'),
        z.literal('mpumalanga'),
        z.literal('free state'),
      ]),
    )
    .nullish(),
  locations: z.array(z.string()).nullish(),
  dealership: z.array(z.string()).nullish(),
  fueltype: z.array(z.string()).nullish(),
  fueleconomyfrom: z.number().nullish(),
  fueleconomyto: z.number().nullish(),
  transmission: z.array(z.string()).nullish(),
  drivetype: z.array(z.number()).nullish(),
  features: z.array(z.string()).nullish(),
  search: z.string().nullish(),
  type: z
    .union([
      z.literal('all'),
      z.literal('used'),
      z.literal('demo'),
      z.literal('konfidence'),
    ])
    .catch('all'),
});

export type Params = z.infer<typeof ParamsSchema>;

export function getFilterCounts(params: Params) {
  const filterCounts = {
    brandsAndRanges: params.makemodel.length,
    body: params.bodytype.length + params.colours.length + params.doors.length,
    location: (params.province?.length || 0) + (params.dealership?.length || 0),
    price: params.priceto || params.pricefrom ? 1 : 0,
    yearsAndMileage:
      (params.yearto || params.yearfrom ? 1 : 0) +
      (params.mileageto || params.mileagefrom ? 1 : 0),
    features: params.features?.length || 0,
    more:
      (params.fueltype?.length || 0) +
      (params.fueleconomyto || params.fueleconomyfrom ? 1 : 0) +
      (params.transmission?.length || 0) +
      (params.drivetype?.length || 0),
  };

  return filterCounts;
}

function oxfordCommaJoin(items: string[]) {
  const sortedItems = [...items].sort((a, b) => a.localeCompare(b));

  switch (sortedItems.length) {
    case 1:
      return sortedItems[0] || '';
    case 2:
      return sortedItems.join(' and ');
    default:
      return [
        sortedItems.slice(0, sortedItems.length - 1).join(', '),
        sortedItems.at(-1),
      ].join(' and ');
  }
}

function findDealership(
  provincesWithDealerships: DealershipsByProvince,
  dealershipId: string,
) {
  for (let p of provincesWithDealerships) {
    for (let dealership of p.dealerships) {
      if (dealership.id === dealershipId) return dealership;
    }
  }
}

function findFeature(featureId: number) {
  return otherOptions.find(({ id }) => id === featureId)?.name;
}

function formatPrice(price: number) {
  return `R${price}`;
}

function formatMileage(mileage: number) {
  return `${mileage}km`;
}

function formatYear(year: number) {
  return `${year}`;
}

function formatFuelEconomy(fueleconomy: number) {
  return `${fueleconomy} litres/100km`;
}

export function getTitleBrand(params: Params) {
  if (params.makemodel.length === 1) {
    const firstMakeModel = (params.makemodel[0] || '').split('-');

    if (!firstMakeModel[0] || firstMakeModel[0] === 'kia') {
      return 'Kia South Africa';
    } else {
      return `${firstMakeModel[0]} South Africa`;
    }
  }

  return 'Kia South Africa';
}

export async function getDynamicHeading(params: Params) {
  // Force default if searching:
  if (params.search) {
    return 'Used cars for sale';
  }

  const parts: string[] = [];

  if (params.type !== 'all') {
    parts.push(`${params.type}`);
  } else {
    parts.push('Used');
  }

  // We only support 1 color
  if (params.colours.length === 1) {
    parts.push(`${params.colours[0]}`);
  }

  // We only support 1 door type
  if (params.doors.length === 1) {
    const str = params.doors[0] || '';

    // Remove 's' from end of door string
    parts.push(str.substring(0, str.length - 1));
  }

  let hasMakeAndModel = false;

  if (params.drivetype?.length === 1) {
    const drivetype = driveTypeOptions.find(
      (option) => option.id === params.drivetype?.[0],
    );

    if (drivetype) {
      parts.push(`${drivetype.name}`);
    }
  }

  if (params.transmission?.length === 1) {
    parts.push(`${params.transmission[0]}`);
  }

  if (params.fueltype?.length === 1) {
    parts.push(`${params.fueltype[0]}`);
  }

  if (params.makemodel.length === 1) {
    const firstMakeModel = (params.makemodel[0] || '').split('-');

    hasMakeAndModel = firstMakeModel.length === 2;
    if (hasMakeAndModel) {
      firstMakeModel[1] = `${firstMakeModel[1]}s`;
    }
    parts.push(...firstMakeModel);
  }

  // We only support 1 body type
  if (!hasMakeAndModel) {
    if (params.bodytype.length === 1) {
      parts.push(`${params.bodytype[0]}s`);
    } else {
      parts.push('cars');
    }
  }

  if (params.features?.length) {
    const features = params.features
      .map((id) => findFeature(parseInt(id)))
      .filter((item) => typeof item !== 'undefined');

    if (features.length) {
      parts.push('with');

      parts.push(oxfordCommaJoin(features));
    }
  }

  parts.push('for sale');

  if (params.pricefrom && params.priceto) {
    parts.push('between');
    parts.push(
      [formatPrice(params.pricefrom), formatPrice(params.priceto)].join(
        ' and ',
      ),
    );
  } else if (params.pricefrom) {
    parts.push('above');
    parts.push(formatPrice(params.pricefrom));
  } else if (params.priceto) {
    parts.push('below');
    parts.push(formatPrice(params.priceto));
  }

  if (params.mileagefrom && params.mileageto) {
    parts.push('between');
    parts.push(
      [formatMileage(params.mileagefrom), formatMileage(params.mileageto)].join(
        ' and ',
      ),
    );
  } else if (params.mileagefrom) {
    parts.push('above');
    parts.push(formatMileage(params.mileagefrom));
  } else if (params.mileageto) {
    parts.push('below');
    parts.push(formatMileage(params.mileageto));
  }

  if (params.yearfrom && params.yearto) {
    parts.push('between');
    parts.push(
      [formatYear(params.yearfrom), formatYear(params.yearto)].join(' and '),
    );
  } else if (params.yearfrom) {
    parts.push('after');
    parts.push(formatYear(params.yearfrom));
  } else if (params.yearto) {
    parts.push('before');
    parts.push(formatYear(params.yearto));
  }

  if (params.fueleconomyfrom && params.fueleconomyto) {
    parts.push('with fuel economy between');
    parts.push(
      [
        formatFuelEconomy(params.fueleconomyfrom),
        formatFuelEconomy(params.fueleconomyto),
      ].join(' and '),
    );
  } else if (params.fueleconomyfrom) {
    parts.push('with fuel economy from');
    parts.push(formatFuelEconomy(params.fueleconomyfrom));
  } else if (params.fueleconomyto) {
    parts.push('with fuel economy until');
    parts.push(formatFuelEconomy(params.fueleconomyto));
  }

  // Only province
  if (params.province?.length && !params.dealership?.length) {
    parts.push('in');
    parts.push(oxfordCommaJoin(params.province));
  } else if (!params.province?.length && params.dealership?.length) {
    const provinces = await fetchDealershipsByProvince();

    const dealerships = params.dealership?.map((dealershipId) =>
      findDealership(provinces, dealershipId),
    );

    const cities = Array.from(
      new Set(
        dealerships
          ?.filter((item) => !!item)
          .map((dealership) => dealership?.city?.name)
          .filter((item) => typeof item !== 'undefined') || [],
      ),
    );

    parts.push('in');
    parts.push(oxfordCommaJoin(cities.filter((city) => !!city)));
  }

  return parts.join(' ');
}

type MakeModelFilter = {
  _and: readonly [
    {
      brand_id: {
        name: {
          _icontains: string;
        };
      };
    },
    {
      range_id: {
        name: {
          _icontains: string;
        };
      };
    },
  ];
};

type MakeFilter = {
  brand_id: {
    name: {
      _icontains: string;
    };
  };
};

type BodyTypeFilter = {
  spec_body_type: {
    name: {
      _icontains: string;
    };
  };
};

type ColourFilter = {
  spec_body_colors: {
    name: {
      _icontains: string;
    };
  };
};

type FuelTypeFilter = {
  spec_fuel_type: {
    name: {
      _icontains: string;
    };
  };
};

type TransmissionFilter = {
  spec_transmission: {
    name: {
      _icontains: string;
    };
  };
};

type DriveTrainFilter = {
  spec_drivetrain: {
    _in: number[];
  };
};

type DoorsFilter = {
  spec_doors: {
    name: {
      _icontains: string;
    };
  };
};

type ActiveFilter = {
  status: {
    _eq: 'active';
  };
};

type NotHideFilter = {
  is_hide_on_website: { _eq: false };
};

type DealershipFilter = {
  dealership_id: {
    _in: string[];
  };
};

type DealershipProvinceFilter = {
  dealership_id: {
    province: {
      name: {
        _icontains: string;
      };
    };
  };
};

type PriceFilter = {
  display_price:
    | {
        _gte: number;
      }
    | {
        _lte: number;
      };
};

type YearFilter = {
  year: {
    _in: number[];
  };
};

type MileageFilter = {
  mileage:
    | {
        _gte: number;
      }
    | {
        _lte: number;
      };
};

type FuelEconomyFilter = {
  spec_litres_100km:
    | {
        _gte: number;
      }
    | {
        _lte: number;
      };
};

type FeaturesFilter = {
  spec_features: {
    spec_features_id: {
      id: {
        _eq: string;
      };
    };
  };
};

type TypeFilter = {
  type:
    | {
        _eq: string;
      }
    | {
        _in: string[];
      };
};

type KonfidenceFilter = {
  is_konfidence: {
    _eq: true;
  };
};

type OrFilter<T> = {
  _or: T[];
};

type AndFilter<T> = {
  _and: T[];
};

type InFilter<T> = {
  [K in keyof T]: {
    _in: string[] | number[];
  };
};
type ParamFilter = {
  _and: (
    | AndFilter<FeaturesFilter>
    | OrFilter<BodyTypeFilter>
    | OrFilter<ColourFilter>
    | OrFilter<FuelTypeFilter>
    | OrFilter<TransmissionFilter>
    | OrFilter<MakeModelFilter | MakeFilter>
    | OrFilter<OrFilter<DealershipProvinceFilter> | DealershipFilter>
    | OrFilter<DoorsFilter>
    | InFilter<DriveTrainFilter>
    | NotHideFilter
    | ActiveFilter
    | TypeFilter
    | FuelEconomyFilter
    | PriceFilter
    | YearFilter
    | MileageFilter
    | FuelEconomyFilter
    | KonfidenceFilter
  )[];
};

type NameFilter<T> = {
  [K in keyof T]: {
    name: { _icontains: string };
  };
};

function makeOneOfFilter<T extends NameFilter<T>>(key: keyof T, values: any[]) {
  return {
    _or: values.map(
      (value) =>
        ({
          [key as any]: {
            name: {
              _icontains: value,
            },
          },
        }) as T,
    ),
  };
}

function makeInFilter<T extends InFilter<T>>(
  key: keyof T,
  values: number[] | string[],
) {
  return {
    [key]: {
      _in: values,
    },
  } as T;
}

export function directusFilterFromSearchParams(
  searchParams: z.infer<typeof ParamsSchema>,
): ParamFilter {
  const filter: ParamFilter = {
    _and: [
      {
        status: {
          _eq: 'active',
        },
      },
      {
        is_hide_on_website: { _eq: false },
      },
    ],
  };

  /*
   * Body type
   */
  if (searchParams.bodytype.length) {
    filter._and.push(
      makeOneOfFilter<BodyTypeFilter>('spec_body_type', searchParams.bodytype),
    );
  }

  /*
   * Colours
   */
  if (searchParams.colours.length) {
    filter._and.push(
      makeOneOfFilter<ColourFilter>('spec_body_colors', searchParams.colours),
    );
  }

  /*
   * Fuel type
   */
  if (searchParams.fueltype?.length) {
    filter._and.push(
      makeOneOfFilter<FuelTypeFilter>('spec_fuel_type', searchParams.fueltype),
    );
  }

  /*
   * Transmission
   */
  if (searchParams.transmission?.length) {
    filter._and.push(
      makeOneOfFilter<TransmissionFilter>(
        'spec_transmission',
        searchParams.transmission,
      ),
    );
  }

  /*
   * Drive train
   */
  if (searchParams.drivetype?.length) {
    filter._and.push(
      makeInFilter<DriveTrainFilter>('spec_drivetrain', searchParams.drivetype),
    );
  }

  /*
   * Doors
   */
  if (searchParams.doors?.length) {
    filter._and.push(
      makeOneOfFilter<DoorsFilter>('spec_doors', searchParams.doors),
    );
  }

  /*
   * Province / Dealership
   */
  if (searchParams.province?.length || searchParams.dealership?.length) {
    const dealershipFilters = searchParams.dealership?.length
      ? [
          {
            dealership_id: {
              _in: searchParams.dealership || [],
            },
          },
        ]
      : [];

    if (searchParams.province?.length) {
      filter._and.push({
        _or: [
          {
            _or: searchParams.province?.map((province) => ({
              dealership_id: {
                province: {
                  name: {
                    _icontains: province,
                  },
                },
              },
            })),
          },
          ...dealershipFilters,
        ],
      } as OrFilter<DealershipFilter | OrFilter<DealershipProvinceFilter>>);
    } else {
      filter._and.push({
        _or: dealershipFilters,
      });
    }
  }

  /*
   * Price
   */
  if (searchParams.pricefrom) {
    filter._and.push({
      display_price: {
        _gte: searchParams.pricefrom,
      },
    });
  }

  if (searchParams.priceto) {
    filter._and.push({
      display_price: {
        _lte: searchParams.priceto,
      },
    });
  }

  /*
   * Year
   */

  const years: number[] = [];
  const yearFrom = searchParams.yearfrom || 1999;
  const yearTo = searchParams.yearto || new Date().getFullYear();

  for (let year = yearFrom; year <= yearTo; year++) {
    years.push(year);
  }

  if (searchParams.yearfrom || searchParams.yearto) {
    filter._and.push({
      year: { _in: years },
    });
  }

  /*
   * Mileage
   */
  if (searchParams.mileagefrom) {
    filter._and.push({
      mileage: {
        _gte: searchParams.mileagefrom,
      },
    });
  }

  if (searchParams.mileageto) {
    filter._and.push({
      mileage: {
        _lte: searchParams.mileageto,
      },
    });
  }

  /*
   * Fuel economy
   */
  if (searchParams.fueleconomyfrom) {
    filter._and.push({
      spec_litres_100km: {
        _gte: searchParams.fueleconomyfrom,
      },
    });
  }

  if (searchParams.fueleconomyto) {
    filter._and.push({
      spec_litres_100km: {
        _lte: searchParams.fueleconomyto,
      },
    });
  }

  /*
   * Features
   */
  if (searchParams.features?.length) {
    filter._and.push({
      _and: searchParams.features.map((feature) => ({
        spec_features: {
          spec_features_id: {
            id: {
              _eq: feature,
            },
          },
        },
      })),
    });
  }

  /*
   * Type
   */
  if (searchParams.type !== 'all') {
    if (searchParams.type === 'konfidence') {
      filter._and.push({
        is_konfidence: {
          _eq: true,
        },
      });
    } else {
      filter._and.push({
        type: {
          _eq:
            searchParams.type === 'used' ? VehicleType.Used : VehicleType.Demo,
        },
      });
    }
  } else {
    filter._and.push({
      type: {
        _in: [VehicleType.Used, VehicleType.Demo, VehicleType.Konfidence],
      },
    });
  }

  /**
   * Make / make
   */

  if (searchParams.makemodel.length) {
    filter._and.push({
      _or: searchParams.makemodel
        .map((makemodel) => {
          const elements = makemodel.split('-');
          const make = elements[0];
          const model = elements[1];

          if (!make && !model) {
            return;
          }

          if (!model) {
            return {
              brand_id: {
                name: {
                  _icontains: make,
                },
              },
            };
          }

          return {
            _and: [
              {
                brand_id: {
                  name: {
                    _icontains: make,
                  },
                },
              },
              {
                range_id: {
                  name: {
                    _icontains: model,
                  },
                },
              },
            ] as const,
          };
        })
        .filter((el) => !!el),
    } as OrFilter<MakeModelFilter | MakeFilter>);
  }

  return filter;
}
